import React from 'react';
import { Sparkles } from 'lucide-react';
import { isOverrideActive } from '../../utils/effectiveNextServicePrice';

/**
 * OverrideChip — compact pill rendered on upcoming work-order surfaces
 * (Dispatch pop card, Mobile job detail, Today mobile card, WorkOrder header).
 *
 * Returns `null` when:
 *   • override is null / undefined / consumed
 *   • bound to a task and the rendering task does not match the binding
 *
 * For unbound (float) overrides the chip renders on the lead's earliest
 * live recurring task — caller is responsible for passing `matchesEarliest`.
 */
export default function OverrideChip({
  override,
  recurringAmount = null, // for "was $X" context
  taskId = null,           // current task being rendered (optional)
  matchesEarliest = false, // true if this task is the earliest live recurring task
  className = '',
  testId = 'override-chip',
}) {
  if (!isOverrideActive(override)) return null;

  const bound = override.appliesToWorkOrderId;

  // Bound override → render only on the matching task.
  if (bound) {
    if (taskId && bound !== taskId) return null;
  } else {
    // Unbound (float) → render only on the earliest live task surface.
    if (taskId && !matchesEarliest) return null;
  }

  const amount = Number(override.amount).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: override.amount % 1 === 0 ? 0 : 2,
  });
  const was = recurringAmount != null && Number(recurringAmount) > 0
    ? Number(recurringAmount).toLocaleString('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: recurringAmount % 1 === 0 ? 0 : 2,
      })
    : null;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 ${className}`}
      data-testid={testId}
      title={override.reason ? `Reason: ${override.reason}` : undefined}
    >
      <Sparkles className="h-3 w-3 text-amber-600 shrink-0" />
      <span>
        Override {amount}
        {was && (
          <span className="text-amber-600 font-normal ml-1">
            (was {was})
          </span>
        )}
      </span>
    </span>
  );
}
